import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const AlertContext = createContext();

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

export const AlertProvider = ({ children }) => {
    const [alertState, setAlertState] = useState({
        isOpen: false,
        message: '',
        type: 'success', // success, error, info, warning
        title: 'Swift2ai',
    });

    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        message: '',
        title: 'Confirm Action',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        type: 'warning', // warning, danger, info
        onConfirm: null,
        onCancel: null,
    });

    const showAlert = useCallback((message, type = 'success', title = 'Swift2ai') => {
        setAlertState({
            isOpen: true,
            message,
            type,
            title,
        });
    }, []);

    const hideAlert = useCallback(() => {
        setAlertState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const showConfirm = useCallback((message, options = {}) => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                message,
                title: options.title || 'Confirm Action',
                confirmText: options.confirmText || 'Confirm',
                cancelText: options.cancelText || 'Cancel',
                type: options.type || 'warning',
                onConfirm: () => {
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                    resolve(false);
                },
            });
        });
    }, []);

    const hideConfirm = useCallback(() => {
        if (confirmState.onCancel) {
            confirmState.onCancel();
        }
    }, [confirmState]);

    return (
        <AlertContext.Provider value={{ showAlert, hideAlert, showConfirm }}>
            {children}

            {/* Global Alert Component */}
            {alertState.isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px] transition-opacity"
                        onClick={hideAlert}
                    />

                    {/* Alert Modal */}
                    <div className="relative bg-white rounded-3xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[340px] overflow-hidden transform transition-all animate-scaleIn p-8 text-center">

                        {/* Logo */}
                        <div className="mb-6 flex justify-center">
                            <img src="/logo.png" alt="Swift2ai Logo" className="h-14 w-auto object-contain" />
                        </div>

                        {/* Animated Icon Container */}
                        <div className="relative mb-6">
                            {/* Ripple Effect for Success */}
                            {alertState.type === 'success' && (
                                <>
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-green-100/50 rounded-full animate-ripple" />
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-green-100/30 rounded-full animate-ripple [animation-delay:0.5s]" />
                                </>
                            )}

                            {/* Icon Background */}
                            <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center relative z-10 transition-transform duration-500 ${alertState.type === 'error' ? 'bg-red-50 text-red-500' :
                                alertState.type === 'info' ? 'bg-blue-50 text-blue-500' :
                                    alertState.type === 'warning' ? 'bg-amber-50 text-amber-500' :
                                        'bg-green-50 text-green-500 shadow-[0_0_20px_rgba(34,197,94,0.2)] animate-successPop'
                                }`}>
                                {alertState.type === 'error' ? (
                                    <div className="animate-errorShake">
                                        <AlertCircle size={40} strokeWidth={2.5} />
                                    </div>
                                ) : alertState.type === 'info' ? (
                                    <Info size={40} strokeWidth={2.5} />
                                ) : alertState.type === 'warning' ? (
                                    <AlertTriangle size={40} strokeWidth={2.5} />
                                ) : (
                                    <div className="animate-checkmarkBounce">
                                        <CheckCircle size={40} strokeWidth={2.5} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Content */}
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {alertState.title}
                        </h3>
                        <p className="text-sm text-gray-500 leading-relaxed mb-8 px-2 font-medium">
                            {alertState.message}
                        </p>

                        {/* Action Button */}
                        <button
                            onClick={hideAlert}
                            className={`w-full py-3.5 rounded-2xl text-[13px] font-black uppercase tracking-widest text-white shadow-lg transition-all transform active:scale-[0.98] hover:shadow-xl ${alertState.type === 'error' ? 'bg-red-500 hover:bg-red-600 shadow-red-200' :
                                alertState.type === 'info' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' :
                                    alertState.type === 'warning' ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-200' :
                                        'bg-[#1a1a1a] hover:bg-black shadow-gray-200'
                                }`}
                        >
                            Okay, Got it
                        </button>
                    </div>
                </div>
            )}

            {/* Global Confirm Dialog */}
            {confirmState.isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px] transition-opacity"
                        onClick={hideConfirm}
                    />

                    {/* Confirm Modal */}
                    <div className="relative bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full max-w-[380px] overflow-hidden transform transition-all animate-scaleIn p-6 text-center">

                        {/* Logo */}
                        <div className="mb-5 flex justify-center">
                            <img src="/logo.png" alt="Swift2ai Logo" className="h-14 w-auto object-contain" />
                        </div>

                        {/* Icon */}
                        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${confirmState.type === 'danger' ? 'bg-red-50 text-red-500' :
                            confirmState.type === 'info' ? 'bg-blue-50 text-blue-500' :
                                'bg-amber-50 text-amber-500'
                            }`}>
                            {confirmState.type === 'danger' ? <AlertCircle size={32} strokeWidth={2.5} /> :
                                confirmState.type === 'info' ? <Info size={32} strokeWidth={2.5} /> :
                                    <AlertTriangle size={32} strokeWidth={2.5} />}
                        </div>

                        {/* Content */}
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {confirmState.title}
                        </h3>
                        <p className="text-sm text-gray-500 leading-relaxed mb-6">
                            {confirmState.message}
                        </p>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={confirmState.onCancel}
                                className="flex-1 py-3 rounded-2xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all transform active:scale-[0.98]"
                            >
                                {confirmState.cancelText}
                            </button>
                            <button
                                onClick={confirmState.onConfirm}
                                className={`flex-1 py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-all transform active:scale-[0.98] hover:-translate-y-0.5 ${confirmState.type === 'danger' ? 'bg-red-500 hover:bg-red-600 shadow-red-100' :
                                    confirmState.type === 'info' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-100' :
                                        'bg-amber-500 hover:bg-amber-600 shadow-amber-100'
                                    }`}
                            >
                                {confirmState.confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
                @keyframes successPop {
                    0% { transform: scale(0); opacity: 0; }
                    60% { transform: scale(1.1); }
                    100% { transform: scale(1); opacity: 1; }
                }
                @keyframes checkmarkBounce {
                    0% { transform: scale(0); }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }
                @keyframes ripple {
                    0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
                    100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
                }
                @keyframes errorShake {
                    0%, 100% { transform: translateX(0); }
                    20%, 60% { transform: translateX(-5px); }
                    40%, 80% { transform: translateX(5px); }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out forwards;
                }
                .animate-scaleIn {
                    animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                .animate-successPop {
                    animation: successPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
                }
                .animate-checkmarkBounce {
                    animation: checkmarkBounce 0.4s 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
                }
                .animate-ripple {
                    animation: ripple 1.5s infinite;
                }
                .animate-errorShake {
                    animation: errorShake 0.4s ease-in-out;
                }
            `}</style>
        </AlertContext.Provider>
    );
};

export default AlertContext;
